import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import React, { useState, useEffect } from "react";
import { Outlet, useOutletContext, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "@/api/axios";

// ----------------------------------------------------------------------
// HELPER FUNCTIONS: Using localStorage for persistence
// ----------------------------------------------------------------------

const getStoredState = (key, defaultValue) => {
    try {
        const storedValue = localStorage.getItem(key);
        return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (error) {
        console.error(`Error retrieving ${key} from localStorage:`, error);
        return defaultValue;
    }
};

const setStoredState = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error(`Error storing ${key} in localStorage:`, error);
    }
};

const SidebarLayout = () => {
    // State for time range and bin selection
    const [timeRangeDropdownOpen, setTimeRangeDropdownOpen] = useState(false);
    const [binDropdownOpen, setBinDropdownOpen] = useState(false);
    const [userRole, setUserRole] = useState("Basic");
    const navigate = useNavigate();
    const location = useLocation();

    // Define time range options
    const timeRangeOptions = ["Day", "Week", "Month", "3 Months", "6 Months", "Year"];

    // State for selected time range and bins - use localStorage
    const [selectedTimeRange, setSelectedTimeRange] = useState(
        () => getStoredState('selectedTimeRange', timeRangeOptions[2])
    );
    const [selectedBins, setSelectedBins] = useState(
        () => getStoredState('selectedBins', [])
    );
    const [allBins, setAllBins] = useState([]);

    // State for year selection - use localStorage
    const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(
        () => getStoredState('selectedYear', new Date().getFullYear().toString())
    );
    const yearOptions = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

    // Revert waste type options to match original values used in components
    const wasteTypeOptions = ["pmd", "organic", "paper", "residual"];

    // State for waste type selection - use localStorage
    const [wasteTypeDropdownOpen, setWasteTypeDropdownOpen] = useState(false);
    const [selectedWasteTypes, setSelectedWasteTypes] = useState(
        () => getStoredState('selectedWasteTypes', [])
    );

    // State for cost savings page - use localStorage
    const [selectedWaste, setSelectedWaste] = useState(
        () => getStoredState('selectedWaste', 'pmd')
    );

    // NEW: State for custom date range - use localStorage
    const [customDateRange, setCustomDateRange] = useState(
        () => getStoredState('customDateRange', [{
            startDate: null,
            endDate: null,
            key: 'selection'
        }])
    );
    const [isCustomDateRange, setIsCustomDateRange] = useState(
        () => getStoredState('isCustomDateRange', false)
    );

    // ----------------------------------------------------------------------
    // EFFECTS: Sync state to localStorage
    // ----------------------------------------------------------------------

    useEffect(() => {
        setStoredState('selectedTimeRange', selectedTimeRange);
    }, [selectedTimeRange]);

    useEffect(() => {
        setStoredState('selectedBins', selectedBins);
    }, [selectedBins]);

    useEffect(() => {
        setStoredState('selectedYear', selectedYear);
    }, [selectedYear]);

    useEffect(() => {
        setStoredState('selectedWasteTypes', selectedWasteTypes);
    }, [selectedWasteTypes]);

    useEffect(() => {
        setStoredState('selectedWaste', selectedWaste);
    }, [selectedWaste]);

    useEffect(() => {
        setStoredState('customDateRange', customDateRange);
    }, [customDateRange]);

    useEffect(() => {
        setStoredState('isCustomDateRange', isCustomDateRange);
    }, [isCustomDateRange]);

    // ----------------------------------------------------------------------
    // DATA FETCHING
    // ----------------------------------------------------------------------

    // Fetch user role
    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const response = await axiosInstance.get("usertier");
                console.log("role: ", response.data.tier);
                setUserRole(response.data.tier);

                if (response.data === "Basic") {
                    if (location.pathname !== '/operations') {
                        navigate('/operations');
                    }
                }
            } catch (error) {
                console.error("Error fetching user role:", error);
            }
        };

        fetchUserRole();
    }, [location.pathname, navigate]);

    // Fetch bin IDs dynamically
    useEffect(() => {
        const fetchBinIds = async () => {
            try {
                console.log("Layout: Fetching bin IDs from API");
                const response = await axiosInstance.get("bin_ids");
                const binIds = response.data || [];
                
                // Validate bin IDs - ensure they're properly formatted
                const validBinIds = binIds.filter(id => id && typeof id === 'string');
                setAllBins(validBinIds);

                // Get stored bin selection from localStorage
                const storedBins = getStoredState('selectedBins', []);

                // If we have stored bins, validate them against current available bins
                if (storedBins.length > 0) {
                    const validStoredBins = storedBins.filter(bin => validBinIds.includes(bin));
                    
                    if (validStoredBins.length > 0) {
                        const finalSelectedBins = validStoredBins.slice(0, Math.min(3, validStoredBins.length));
                        setSelectedBins(finalSelectedBins);
                    } else {
                        // If stored bins are invalid (e.g., user switched), default to first 3
                        const initialBins = validBinIds.slice(0, Math.min(3, validBinIds.length));
                        setSelectedBins(initialBins);
                    }
                } else {
                    // No stored bins, default to first 3
                    const initialBins = validBinIds.slice(0, Math.min(3, validBinIds.length));
                    setSelectedBins(initialBins);
                }
            } catch (error) {
                console.error("Layout: Error fetching bin IDs:", error);
            }
        };

        fetchBinIds();
    }, []); 

    // ----------------------------------------------------------------------
    // HANDLERS
    // ----------------------------------------------------------------------

    const selectTimeRange = (timeRange) => {
        setSelectedTimeRange(timeRange);
        return false; 
    };

    const toggleBinSelection = (bin) => {
        setSelectedBins(prev => {
            const newValue = prev.includes(bin)
                ? prev.filter(item => item !== bin)
                : [...prev, bin];
            return newValue;
        });
        return false; 
    };

    const selectYear = (year) => {
        setSelectedYear(year);
        return false; 
    };

    const toggleWasteTypeSelection = (wasteType) => {
        setSelectedWasteTypes(prev => {
            const newValue = prev.includes(wasteType)
                ? prev.filter(item => item !== wasteType)
                : [...prev, wasteType];
            return newValue;
        });
        return false; 
    };

    const selectWaste = (waste) => {
        setSelectedWaste(waste);
    };

    const handleCustomDateRangeChange = (dateRange, isCustom) => {
        setCustomDateRange(dateRange);
        setIsCustomDateRange(isCustom);
        
        if (!isCustom) {
            setSelectedTimeRange(timeRangeOptions[2]); 
        }
    };

    const headerProps = {
        timeRangeDropdownOpen,
        setTimeRangeDropdownOpen,
        binDropdownOpen,
        setBinDropdownOpen,
        selectedTimeRange,
        selectTimeRange,
        timeRangeOptions,
        selectedBins,
        allBins,
        toggleBinSelection,
        yearDropdownOpen,
        setYearDropdownOpen,
        selectedYear,
        selectYear,
        yearOptions,
        wasteTypeDropdownOpen,
        setWasteTypeDropdownOpen,
        selectedWasteTypes,
        toggleWasteTypeSelection,
        wasteTypeOptions,
        selectedWaste,
        setSelectedWaste: selectWaste,
        customDateRange,
        onCustomDateRangeChange: handleCustomDateRangeChange,
        isCustomDateRange,
    };

    return (
        <SidebarProvider>
            <AppSidebar variant="inset" userRole={userRole} />
            <SidebarInset>
                {/* Header is rendered unless on specific full-page views */}
                {location.pathname !== "/cost-savings" && location.pathname !== "/circularity" && (
                    <SiteHeader {...headerProps} />
                )}

                {/* Spacer */}
                {!location.pathname.includes('/account') && location.pathname !== "/cost-savings" && location.pathname !== "/circularity" && (
                    <div className="h-4 md:h-6"></div>
                )}

                <div className="tab-content h-full">
                    <Outlet context={{
                        ...headerProps,
                        selectedTimeRange,
                        selectedBins,
                        selectedYear,
                        selectedWasteTypes,
                        selectedWaste,
                        userRole,
                        customDateRange,
                        isCustomDateRange,
                    }} />
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
};

export default SidebarLayout;

// Helper hook
export function useFilters() {
    try {
        return useOutletContext() || {
            selectedTimeRange: null,
            selectedBins: null,
            selectedYear: null,
            selectedWasteTypes: null,
            selectedWaste: null,
            userRole: null,
            customDateRange: null,
            isCustomDateRange: false,
        };
    } catch (error) {
        return {
            selectedTimeRange: null,
            selectedBins: null,
            selectedYear: null,
            selectedWasteTypes: null,
            selectedWaste: null,
            userRole: null,
            customDateRange: null,
            isCustomDateRange: false,
        };
    }
}